import {
	isFailedSubagentStatus,
	isTerminalSubagentStatus,
	subagentTotalInputTokens,
	type ChatSubagentRun,
	type ChatWorkflowRun,
	type SubagentRunStatus,
} from "./chat";

/** ノードの表示状態。"pending" は run 行がまだ無い (空き枠待ち・上流待ち) ノード。 */
export type WorkflowNodeState = SubagentRunStatus | "pending";

/** workflow の 1 ノード。宣言と run 行を突き合わせた結果で、run は起きていなければ null。 */
export interface WorkflowNodeEntry {
	id: string;
	description: string;
	run: ChatSubagentRun | null;
}

export interface WorkflowPhaseGroup {
	/** 同じ workflow 内で一意。表示には使わない。 */
	key: string;
	/** null はフェーズ指定の無いノードの束。 */
	title: string | null;
	nodes: WorkflowNodeEntry[];
	completed: number;
	total: number;
	hasActive: boolean;
	/** 異常終了した run を含むか (ユーザーによる停止は除く)。 */
	hasFailure: boolean;
}

export interface WorkflowTaskGroup {
	workflow: ChatWorkflowRun;
	/** 宣言順。フェーズ指定の無いノードは末尾の title=null の束に入る。 */
	phases: WorkflowPhaseGroup[];
	/** フェーズ順に並べた全ノード。進捗ドットはこの順で描く。 */
	nodes: WorkflowNodeEntry[];
	/** 所有する run の入出力トークン合計。run 行の無いノードは 0。 */
	totalTokens: number;
}

/** ノードの表示状態。run 行が無ければ pending。 */
export function workflowNodeState(entry: WorkflowNodeEntry): WorkflowNodeState {
	return entry.run?.status ?? "pending";
}

/**
 * 宣言ノードを左辺にして所有 run を join し、フェーズ単位へ束ねる。run 行はプロファイルの空き枠が
 * 出たノードから順に作られるため、まだ起きていないノードは宣言からしか分からない。逆に、宣言に無い
 * run (列の追加より前の行、または投入時の定義と食い違う版ずれ) も落とさず、宣言済みの後ろへ回す。
 */
export function groupWorkflowNodes(
	workflow: ChatWorkflowRun,
	runs: readonly ChatSubagentRun[],
): WorkflowTaskGroup {
	const owned = runs.filter((run) => run.workflowRunId === workflow.id);
	const byLabel = new Map(owned.filter((run) => run.label !== null).map((run) => [run.label, run]));
	const declared = workflow.declaredNodes.map((node) => ({
		phase: node.phase,
		entry: { id: node.id, description: node.description, run: byLabel.get(node.id) ?? null },
	}));
	const declaredIds = new Set(workflow.declaredNodes.map((node) => node.id));
	const undeclared = owned
		.filter((run) => run.label === null || !declaredIds.has(run.label))
		.sort((a, b) => a.createdAt.localeCompare(b.createdAt))
		.map((run) => ({
			phase: run.phase,
			entry: { id: run.label ?? run.id, description: run.description, run },
		}));

	const byPhase = new Map<string | null, WorkflowNodeEntry[]>();
	for (const title of workflow.declaredPhases) byPhase.set(title, []);
	for (const { phase, entry } of [...declared, ...undeclared]) {
		const bucket = byPhase.get(phase) ?? [];
		bucket.push(entry);
		byPhase.set(phase, bucket);
	}
	// フェーズ指定の無い束は末尾へ。Map は挿入順なので、宣言済み → 宣言に無い順 → null の順になる
	// ように一度外して入れ直す。
	const unphased = byPhase.get(null);
	byPhase.delete(null);
	if (unphased !== undefined) byPhase.set(null, unphased);

	const phases = [...byPhase.entries()].map(([title, nodes]) => summarizePhase(title, nodes));
	const nodes = phases.flatMap((phase) => phase.nodes);
	return {
		workflow,
		phases,
		nodes,
		totalTokens: owned.reduce(
			(sum, run) => sum + subagentTotalInputTokens(run) + run.outputTokens,
			0,
		),
	};
}

function summarizePhase(title: string | null, nodes: WorkflowNodeEntry[]): WorkflowPhaseGroup {
	const runs = nodes.flatMap((node) => (node.run === null ? [] : [node.run]));
	const completed = runs.filter((run) => isTerminalSubagentStatus(run.status)).length;
	return {
		key: title ?? "",
		title,
		nodes,
		completed,
		total: nodes.length,
		hasActive: runs.some((run) => !isTerminalSubagentStatus(run.status)),
		hasFailure: runs.some((run) => isFailedSubagentStatus(run.status)),
	};
}
